import { ErrorResponse } from "@rossum/upcloud-costs-shared";

/** An API error, carrying the enumerated code to translate and the technical detail to log. */
export class ApiError extends Error {
    constructor(
        readonly status: number,
        readonly code: string,
        readonly details?: string,
    ) {
        super(`${String(status)} ${code}${details === undefined ? "" : `: ${details}`}`);
        this.name = "ApiError";
    }
}

type QueryValue = string | readonly string[] | undefined;

function url(path: string, query?: Record<string, QueryValue>): string {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query ?? {})) {
        for (const item of value === undefined ? [] : typeof value === "string" ? [value] : value) {
            params.append(key, item);
        }
    }
    const search = params.toString();
    return search === "" ? path : `${path}?${search}`;
}

async function request<T>(method: string, path: string, query?: Record<string, QueryValue>): Promise<T> {
    const response = await fetch(url(path, query), {
        method,
        credentials: "same-origin",
        headers: { Accept: "application/json" },
    });
    if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as Partial<ErrorResponse>;
        throw new ApiError(response.status, body.code ?? "INTERNAL_ERROR", body.details);
    }
    return (await response.json()) as T;
}

/** Reads a resource of the backend API. */
export function getJson<T>(path: string, query?: Record<string, QueryValue>): Promise<T> {
    return request<T>("GET", path, query);
}

/** Posts to the backend API; the browser adds the Origin header the backend checks. */
export function postJson<T>(path: string): Promise<T> {
    return request<T>("POST", path);
}
